import { WorkspaceViewArticleItem } from '@/@types/WorkspaceViewArticleItem';
import { Article } from '@/models/article/Article';
import { Logger } from '@/other/Logger';
import apiService from '@/services/ApiService';
import { workspaceService } from '@/services/workspace/WorkspaceService';
import { annotatePainted } from './annotatePainted';
import type { BuildResult } from './local-renderer';
import { resignStoredHtml } from './resignStoredHtml';

const log = new Logger('pdf:in-browser');

const EDITOR_CONTENT_SELECTOR = '.el-tiptap-editor__content';

/** The editor holds a loaded Article; the sidebar only has the row it renders. */
export type ExportTarget = Article | WorkspaceViewArticleItem;

/**
 * The painted editor is the better source than the stored tab HTML: ImageView re-signs
 * expired S3 urls into the node attrs as it resolves them, so only the live DOM is
 * guaranteed to hold urls that still load.
 *
 * Only for the article that editor is actually showing, though. The sidebar exports any row
 * in the list, and one mounted editor would otherwise hand every one of them the open
 * article's content (SNAP-2257).
 */
const paintedHtml = (article: Article): string | null => {
  if (workspaceService.workspaceView.selectedArticle?.id !== article.id) return null;

  const content = document.querySelector(EDITOR_CONTENT_SELECTOR);
  return content ? annotatePainted(content) : null;
};

/**
 * The sidebar exports a row of the workspace article list, which carries no textTabs, no
 * createdAt and no slug getter. Without the full article it would export an empty note and
 * save it as article.pdf (SNAP-2257).
 */
export const resolveArticle = async (target: ExportTarget): Promise<Article> => {
  if (target instanceof Article) return target;

  const selected = workspaceService.workspaceView.selectedArticle;
  if (selected?.id === target.id) return selected;

  const response = await apiService.article.get(target.id);
  return Article.fromJson(response.data.article);
};

const storedHtml = (article: Article, textTabId?: string): string => {
  const tabs = article.textTabs ?? [];
  const tab = tabs.find((item) => item.id === textTabId) ?? tabs[0];
  return tab?.text ?? '';
};

const fileName = (article: Article) => `${article.slug || 'article'}.pdf`;

/** Only iOS and iPadOS actually need the share sheet; everywhere else can save a blob. */
const needsShareSheet = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const saveViaAnchor = (blob: Blob, name: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

/**
 * iOS Safari displays a blob: pdf and refuses to save it (WebKit 190351). The share sheet is
 * the only delivery that works there, and it needs transient activation, which any await
 * before the call has already spent. So the tab is claimed inside the click and the bytes are
 * pushed into it when they are ready.
 */
export const exportArticlePdfInBrowser = async (target: ExportTarget, textTabId?: string): Promise<BuildResult> => {
  // window.open must happen before the first await, or the share sheet loses transient
  // activation and never opens.
  const viewer = needsShareSheet() ? window.open('', '_blank') : null;
  if (viewer) viewer.document.write('<title>Rendering PDF…</title><p>Rendering PDF…</p>');

  try {
    const article = await resolveArticle(target);
    const painted = paintedHtml(article);
    const html = painted ?? storedHtml(article, textTabId);
    if (!html.trim()) throw new Error('Article has no note content to export');

    const source = painted ? html : await resignStoredHtml(html);
    // Lazy so that yoga, fontkit and pdfkit never land in the main bundle.
    const { buildArticlePdf } = await import('./local-renderer');
    const result = await buildArticlePdf({ title: article.title, createdAt: article.createdAt, html: source });

    log.info(`rendered ${(result.blob.size / 1024).toFixed(0)} KB in ${result.durationMs.toFixed(0)} ms`);
    if (result.failedImages.length) log.error(`images failed: ${result.failedImages.join(', ')}`);

    if (viewer) viewer.location.href = URL.createObjectURL(result.blob);
    else saveViaAnchor(result.blob, fileName(article));

    return result;
  } catch (error) {
    viewer?.close();
    throw error;
  }
};

export default exportArticlePdfInBrowser;
